import { type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from 'qampo-ui-library';
import type {
  InterventionGroupTemplateResponse,
  InterventionTemplateResponse
} from '../../../api/generated';
import { PatientLocationType } from '../../../api/generated';
import { IconTrash } from '../../UI';
import { InterventionIconComponent } from '../../InterventionIcon/InterventionIcon';
import { InterventionIconPickerPanel } from '../../InterventionIcon/InterventionIconPickerPanel';
import { usePortalDropdown } from '../../../hooks/usePortalDropdown';
import { useKronerInput } from '../../../hooks/useKronerInput';
import { InterventionGraphEditor } from '../InterventionGraphEditor';
import { TEMPLATE_FIELD_PATH } from '../../../constants/templateFieldPaths';
import { buildFieldPath } from '../../../utils/fieldErrorUtils';
import { BarSizePicker } from './BarSizePicker';
import styles from '../WeekEditorPanel.module.css';

interface GroupSectionProps {
  group: InterventionGroupTemplateResponse;
  weekIndex: number;
  groupIndex: number;
  catalog: InterventionTemplateResponse[];
  strokeColor: string;
  fillColor: string;
  fieldErrors: Record<string, string>;
  clearFieldError: (path: string) => void;
  onUpdate: (updated: InterventionGroupTemplateResponse) => void;
  onRemove: () => void;
}

export const GroupSection = ({
  group,
  weekIndex,
  groupIndex,
  catalog,
  strokeColor,
  fillColor,
  fieldErrors,
  clearFieldError,
  onUpdate,
  onRemove
}: GroupSectionProps) => {
  const { t } = useTranslation();
  const nameErrorKey = buildFieldPath(
    TEMPLATE_FIELD_PATH.weeks,
    weekIndex,
    TEMPLATE_FIELD_PATH.groups,
    groupIndex,
    TEMPLATE_FIELD_PATH.name
  );
  const nameError = fieldErrors[nameErrorKey];

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (nameError) clearFieldError(nameErrorKey);
    onUpdate({ ...group, name: e.target.value });
  };
  // An empty field leaves the price undecided; a typed 0 makes the group deliberately free.
  const reimbursementInput = useKronerInput(
    group.reimbursementAmount,
    (amount) => onUpdate({ ...group, reimbursementAmount: amount }),
    { maxDecimals: 0, allowZero: true }
  );
  const {
    isOpen: isIconOpen,
    panelStyle: iconPanelStyle,
    triggerRef: iconTriggerRef,
    panelRef: iconPanelRef,
    toggle: toggleIcon,
    close: closeIcon
  } = usePortalDropdown<HTMLButtonElement>();

  const handlePatientTypeChange = (type: PatientLocationType) => {
    onUpdate({ ...group, patientType: type });
  };

  // Show the group's own icon; an unset icon stays empty (no default glyph).
  const groupIconValue = group.icon;

  return (
    <div className={styles.groupSection}>
      <div className={styles.groupHeader}>
        <div className={styles.groupNameField}>
          <div className={`${styles.groupField} ${nameError ? styles.groupFieldError : ''}`}>
            <button
              ref={iconTriggerRef}
              type="button"
              className={`${styles.groupIconButton} ${isIconOpen ? styles.groupIconButtonOpen : ''}`}
              onClick={toggleIcon}
              title={t('config-group-icon')}
            >
              <InterventionIconComponent icon={groupIconValue} width={18} height={18} />
            </button>
            <div className={styles.groupFieldDivider} />

            <input
              className={styles.groupNameInput}
              value={group.name}
              onChange={handleNameChange}
              placeholder={t('config-group-name-placeholder')}
              aria-invalid={!!nameError}
            />
          </div>
          {nameError && <span className={styles.fieldError}>{nameError}</span>}
        </div>

        <div className={styles.groupReimbursementField} title={t('config-group-reimbursement')}>
          <input
            type="number"
            className={styles.groupReimbursementInput}
            value={reimbursementInput.text}
            onChange={reimbursementInput.handleChange}
            onBlur={reimbursementInput.handleBlur}
            onKeyDown={reimbursementInput.handleKeyDown}
            min={500}
            step={500}
            aria-label={t('config-group-reimbursement')}
            placeholder={t('config-group-reimbursement')}
          />
          <span className={styles.groupReimbursementSuffix} aria-hidden="true">
            kr
          </span>
        </div>

        <BarSizePicker
          value={group.barSize}
          strokeColor={strokeColor}
          fillColor={fillColor}
          onChange={(size) => onUpdate({ ...group, barSize: size })}
        />

        <span title={t('config-remove-group')} aria-label={t('config-remove-group')}>
          <IconButton
            icon={<IconTrash />}
            onClick={onRemove}
            size="small"
            classes={styles.deleteIconBtn}
          />
        </span>
      </div>

      <div className={styles.metaRow}>
        <div className={styles.patientTypeToggle}>
          <span className={styles.metaLabel}>{t('config-group-patient-type')}</span>
          <div className={styles.toggleButtons}>
            <button
              className={`${styles.toggleBtn} ${group.patientType === PatientLocationType.Home ? styles.toggleBtnActive : ''}`}
              onClick={() => handlePatientTypeChange(PatientLocationType.Home)}
              aria-pressed={group.patientType === PatientLocationType.Home}
              type="button"
            >
              {t('config-patient-home')}
            </button>
            <button
              className={`${styles.toggleBtn} ${group.patientType === PatientLocationType.Admitted ? styles.toggleBtnActive : ''}`}
              onClick={() => handlePatientTypeChange(PatientLocationType.Admitted)}
              aria-pressed={group.patientType === PatientLocationType.Admitted}
              type="button"
            >
              {t('config-patient-admitted')}
            </button>
          </div>
        </div>
      </div>

      <InterventionIconPickerPanel
        isOpen={isIconOpen}
        panelStyle={iconPanelStyle}
        panelRef={iconPanelRef}
        value={group.icon}
        onSelect={(icon) => {
          onUpdate({ ...group, icon });
          closeIcon();
        }}
      />

      <div className={styles.groupBody}>
        <InterventionGraphEditor
          group={group}
          catalog={catalog}
          strokeColor={strokeColor}
          fillColor={fillColor}
          onChange={onUpdate}
        />
      </div>
    </div>
  );
};
